/**
 * The Jupyter link a Build node opens, moved into the project's own folder.
 *
 * A node stores its notebook link once, when the notebook is made, and the
 * link can point outside the project. A copied project keeps its source
 * project's link, and nodes made before per-project folders say
 * `<company>/notebooks/<file>`. Jupyter's project-scope check refuses both
 * (2026-09-28: every notebook of "Norion NMD model (IRRBB) v2" answered 403
 * "outside project scope", for its owner's company and for a shared member).
 * The notebooks themselves live in the project's folder, the `initial_path`
 * that the project's Jupyter token names, under `notebooks/`.
 *
 * So a link inside the project folder keeps its path. Any other link keeps its
 * file and moves into the project folder: the part after its last
 * `notebooks/`, or else the bare file name at the project root. Either way the
 * link moves to the current Jupyter server, because a stored host can belong
 * to another environment. Dot segments are dropped, so the result stays inside
 * the project folder. A link without `/lab/tree/` is returned unchanged,
 * because its layout is unknown.
 */
const TREE = '/lab/tree/';

export function projectNotebookUrl(
  storedUrl: string,
  serverUrl: string,
  initialPath: string,
): string {
  const at = storedUrl.indexOf(TREE);
  if (at < 0 || !serverUrl || !initialPath) return storedUrl;

  const root = serverUrl.replace(/\/+$/, '');
  const base = '/' + initialPath.replace(/^\/+|\/+$/g, '');
  if (!base.startsWith(TREE)) return storedUrl;
  const projectFolder = base.slice(TREE.length);

  const tail = storedUrl.slice(at + TREE.length);
  const cut = tail.search(/[?#]/);
  const storedPath = (cut >= 0 ? tail.slice(0, cut) : tail).replace(/^\/+|\/+$/g, '');
  const suffix = cut >= 0 ? tail.slice(cut) : '';

  let relative: string;
  if (storedPath === projectFolder || storedPath.startsWith(projectFolder + '/')) {
    relative = storedPath.slice(projectFolder.length).replace(/^\/+/, '');
  } else {
    const inNotebooks = storedPath.match(/^(?:.*\/)?notebooks\/(.+)$/);
    relative = inNotebooks
      ? `notebooks/${inNotebooks[1]}`
      : (storedPath.split('/').filter(Boolean).pop() ?? '');
  }
  // Drop dot segments, also percent-encoded ones, so the result stays inside
  // the project folder. The browser would resolve `..` above it.
  relative = relative.split('/').filter(segment => !isDotSegment(segment)).join('/');

  return relative ? `${root}${base}/${relative}${suffix}` : `${root}${base}${suffix}`;
}

function isDotSegment(segment: string): boolean {
  let decoded = segment;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    // A malformed escape is not a dot segment; keep it as stored.
  }
  return decoded === '' || decoded === '.' || decoded === '..';
}
